import { z } from "zod";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { ListSkeleton } from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const SeoChecklistSchema = z.object({
  items: z.array(
    z.object({
      label: z.string(),
      status: z.enum(["pass", "fail", "warning"]),
      detail: z.string().optional(),
      score: z.number().optional(),
    }),
  ),
  summary: z
    .object({
      passed: z.number(),
      total: z.number(),
    })
    .optional(),
});

type SeoChecklistData = z.infer<typeof SeoChecklistSchema>;

/* ────────────────────── Helpers ────────────────────── */

type CheckStatus = "pass" | "fail" | "warning";

interface StatusConfig {
  icon: React.ComponentType<{ className?: string }>;
  iconClass: string;
  badgeClass: string;
  label: string;
}

const STATUS_CONFIG: Record<CheckStatus, StatusConfig> = {
  pass: {
    icon: CheckCircle2,
    iconClass: "text-teal-500",
    badgeClass:
      "bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-400",
    label: "통과",
  },
  fail: {
    icon: XCircle,
    iconClass: "text-red-500",
    badgeClass: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
    label: "미통과",
  },
  warning: {
    icon: AlertTriangle,
    iconClass: "text-amber-500",
    badgeClass:
      "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
    label: "주의",
  },
};

/* ────────────────────── Summary Bar ────────────────────── */

function SummaryBar({ passed, total }: { passed: number; total: number }) {
  const ratio = total > 0 ? passed / total : 0;
  const pct = Math.round(ratio * 100);
  const barColor =
    ratio >= 0.7
      ? "hsl(var(--signal-success))"
      : ratio >= 0.4
        ? "hsl(var(--signal-warning))"
        : "hsl(var(--signal-danger))";

  return (
    <div className="mb-4 rounded-lg border border-stroke-subtle bg-surface-secondary/30 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-medium text-text-primary">
          검사 결과
        </span>
        <span className="text-[11px] font-semibold" style={{ color: barColor }}>
          {passed} / {total} 통과 ({pct}%)
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-secondary">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
        />
      </div>
    </div>
  );
}

/* ────────────────────── Component ────────────────────── */

export function SeoChecklist({
  widget,
  className,
}: AgentWidgetProps<SeoChecklistData>) {
  const { items, summary } = widget.data;

  // Auto-compute summary if not provided
  const computedSummary = summary ?? {
    passed: items.filter((i) => i.status === "pass").length,
    total: items.length,
  };

  return (
    <div className={cn("w-full", className)}>
      <SummaryBar
        passed={computedSummary.passed}
        total={computedSummary.total}
      />

      <ul className="space-y-1.5">
        {items.map((item, idx) => {
          const cfg = STATUS_CONFIG[item.status];
          const Icon = cfg.icon;

          return (
            <li
              key={idx}
              className="flex items-start gap-2.5 rounded-lg px-2 py-2 hover:bg-surface-secondary/30"
            >
              <Icon className={cn("mt-0.5 size-4 shrink-0", cfg.iconClass)} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-medium text-text-primary">
                    {item.label}
                  </span>
                  {item.score !== undefined && (
                    <span className="text-[10px] text-text-tertiary">
                      ({item.score}점)
                    </span>
                  )}
                </div>
                {item.detail && (
                  <p className="mt-0.5 text-[11px] leading-relaxed text-text-tertiary">
                    {item.detail}
                  </p>
                )}
              </div>
              <span
                className={cn(
                  "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
                  cfg.badgeClass,
                )}
              >
                {cfg.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function SeoChecklistSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-stroke-subtle bg-surface-raised p-4",
        className,
      )}
    >
      {/* Summary bar skeleton */}
      <div className="mb-4 rounded-lg border border-stroke-subtle bg-surface-secondary/30 p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="h-3 w-16 skeleton-shimmer rounded" />
          <div className="h-3 w-24 skeleton-shimmer rounded" />
        </div>
        <div className="h-2 w-full skeleton-shimmer rounded-full" />
      </div>
      <ListSkeleton count={6} />
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const seoChecklistWidget = defineWidget<SeoChecklistData>({
  type: WIDGET_TYPES.SEO_CHECKLIST,
  label: "SEO 체크리스트",
  schema: SeoChecklistSchema,
  component: SeoChecklist,
  skeleton: SeoChecklistSkeleton,
  defaultLayout: "full",
  maxHeight: 360,
  aiHint: "SEO 항목별 통과/미통과/주의 상태 체크리스트와 종합 요약",
});

registerWidget(seoChecklistWidget);
